import { SkeletonRows } from '../../components/LoadingSkeleton/LoadingSkeleton';
// pages/Clients/logsmodal.jsx
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import {
  Activity,
  Camera,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  RefreshCw,
  X
} from 'lucide-react';

export const parse254Vbat = (vbat) => {
  if (!vbat) return null;
  const raw = Array.isArray(vbat) ? vbat.join(',') : String(vbat);
  const packetValues = raw.split(',').pop().split('-').map(value => value.trim());
  const hasId = packetValues.length === 29;
  const values = hasId ? packetValues.slice(1) : packetValues;
  const hasAirVolume = values.length === 28;
  const hasPressure = values.length === 27 || hasAirVolume;
  if (!hasPressure && values.length !== 15) return null;

  // After the optional ID: 27 fields without air volume, or 28 with it.
  // Metadata is followed by ten pressure samples, ten alcohol samples,
  // then peak, AUC and wiper. The last metadata field may be NA.
  const traceStart = hasPressure ? (hasAirVolume ? 5 : 4) : 2;
  const numericFields = hasPressure ? values.slice(traceStart) : values;
  if (numericFields.some(value => value === '' || !Number.isFinite(Number(value)))) return null;
  const alcoholStart = hasPressure ? traceStart + 10 : 2;
  const metricStart = hasPressure ? traceStart + 20 : 12;
  const metadataNumber = value => value !== '' && Number.isFinite(Number(value)) ? Number(value) : '-';

  return {
    hasPressure,
    id: hasId && packetValues[0] !== '' && Number.isFinite(Number(packetValues[0]))
      ? Number(packetValues[0]) : '-',
    blowStartingPressure: metadataNumber(values[0]),
    userBlowValue: metadataNumber(values[1]),
    totalVolume: hasPressure ? metadataNumber(values[2]) : '-',
    airVolume: hasAirVolume ? metadataNumber(values[3]) : '-',
    adcValues: Array.from({ length: 10 }, (_, index) => ({
      sample: index + 1,
      pressure: hasPressure ? Number(values[traceStart + index]) : null,
      alcohol: Number(values[alcoholStart + index]),
    })),
    peakFactor: Number(values[metricStart]),
    auc: Number(values[metricStart + 1]),
    wiperValue: Number(values[metricStart + 2]),
  };
};

const LogsModal = ({ 
  isOpen, 
  onClose, 
  client, 
  logs, 
  logsLoading, 
  onRefresh, 
  onDownload, 
  onViewImage,
  onToggleSort,
  user
}) => {
  const [logsCurrentPage, setLogsCurrentPage] = useState(1);
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(10);
  const [logFilter, setLogFilter] = useState('');
  const [goToPage, setGoToPage] = useState('');
  const [isUnsorted, setIsUnsorted] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [downloadProgress, setDownloadProgress] = useState({ completed: 0, total: 0, included: 0, status: '' });
  const downloadLockRef = useRef(false);
  const [show254Graph, setShow254Graph] = useState(false);
  const [selected254Log, setSelected254Log] = useState(null);

  // The modal component remains mounted when it is closed, so clear any
  // session-specific controls before the next time it opens.
  useEffect(() => {
    if (isOpen) return;

    setLogFilter('');
    setLogsRowsPerPage(10);
    setLogsCurrentPage(1);
    setGoToPage('');
    setIsUnsorted(false);
    setShow254Graph(false);
    setSelected254Log(null);
    setDownloadError('');
    setDownloadProgress({ completed: 0, total: 0, included: 0, status: '' });
  }, [isOpen]);

  // Order the displayed timestamps before filtering and pagination, including
  // when a refresh or another request supplies records in a different order.
  const orderedLogs = useMemo(() => {
    const records = Array.isArray(logs) ? [...logs] : [];
    const timestamp = log => {
      const value = String(log.time || log.rtime || log.rawRtime || '').trim();
      const parsed = moment(value, [
        'MM/DD/YYYY HH:mm:ss', 'MM/DD/YYYY H:mm:ss',
        'MM/DD/YYYY hh:mm:ss A', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601
      ], true);
      return parsed.isValid() ? parsed.valueOf() : -Infinity;
    };
    return records.sort((a, b) => {
      if (!isUnsorted) {
        const aTime = timestamp(a);
        const bTime = timestamp(b);
        if (aTime !== bTime) return aTime > bTime ? -1 : 1;
      }
      return Number(b.id || 0) - Number(a.id || 0);
    });
  }, [logs, isUnsorted]);

 // Filter logs
  const filteredLogs = useMemo(() => {
    if (!logFilter) return orderedLogs;
    const searchLower = logFilter.toLowerCase();
    return orderedLogs.filter(log =>
      log.samplerId?.toLowerCase().includes(searchLower) ||
      log.controlId?.toLowerCase().includes(searchLower) ||
      log.time?.toLowerCase().includes(searchLower) ||
      log.rawRtime?.toLowerCase().includes(searchLower) ||
      log.event?.toLowerCase().includes(searchLower) ||
      String(log.rawEventType ?? log.typeofevent ?? '').toLowerCase().includes(searchLower) ||
      log.result?.toLowerCase().includes(searchLower) ||
      log.image?.toLowerCase().includes(searchLower) ||
      log.imgtime?.toLowerCase().includes(searchLower)
    );
  }, [orderedLogs, logFilter]);

  const totalItems = filteredLogs.length;
  const pageSize = logsRowsPerPage === 'all'
  ? Math.max(1, totalItems)
  : Math.max(1, Number(logsRowsPerPage) || 10);
  // const pageSize = logsRowsPerPage === 'all' ? Math.max(1, totalItems) : logsRowsPerPage;
  const totalLogPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(Math.max(1, logsCurrentPage), totalLogPages);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setLogsCurrentPage(1);
  }, [logFilter]);

  // Reset to page 1 when rows per page changes
  useEffect(() => {
    setLogsCurrentPage(1);
  }, [logsRowsPerPage]);

  // Ensure current page is valid
  useEffect(() => {
    if (logsCurrentPage > totalLogPages) {
      setLogsCurrentPage(totalLogPages);
    }
    if (logsCurrentPage < 1) {
      setLogsCurrentPage(1);
    }
  }, [logsCurrentPage, totalLogPages]);

  // Paginated logs
  const paginatedLogs = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredLogs.slice(start, start + pageSize);
  }, [filteredLogs, currentPage, pageSize]);

  // Yield between batches so selecting All can paint its loader and accept input.
  const [renderBatch, setRenderBatch] = useState({ source: null, count: 0 });
  const renderedCount = renderBatch.source === paginatedLogs ? renderBatch.count : 0;
  const isRenderingAll = logsRowsPerPage === 'all' && renderedCount < paginatedLogs.length;
  const visibleLogs = logsRowsPerPage === 'all'
    ? paginatedLogs.slice(0, renderedCount)
    : paginatedLogs;

  useEffect(() => {
    if (!isOpen || logsLoading || logsRowsPerPage !== 'all' || renderedCount >= paginatedLogs.length) return;
    const timer = setTimeout(() => {
      setRenderBatch({ source: paginatedLogs, count: Math.min(renderedCount + 100, paginatedLogs.length) });
    }, 50);
    return () => clearTimeout(timer);
  }, [isOpen, logsLoading, logsRowsPerPage, paginatedLogs, renderedCount]);

  // Calculate display range
  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalItems);

  useEffect(() => {
    if (!isOpen) return;
    console.log('[LogsModal] Pagination:', {
      rowsPerPage: logsRowsPerPage,
      currentPage,
      totalPages: totalLogPages,
      startIndex,
      endIndex,
      totalItems,
    });
  }, [isOpen, logsRowsPerPage, currentPage, totalLogPages, startIndex, endIndex, totalItems]);
  // Pagination handlers
  const handleLogsPageChange = useCallback((page) => {
    if (page >= 1 && page <= totalLogPages) {
      setLogsCurrentPage(page);
      setGoToPage('');
    }
  }, [totalLogPages]);
const getVbatRowId = (log) => {
  const vbat = log?.rawVbat ?? log?.vbat;
  if (!vbat) return '-';

  const raw = Array.isArray(vbat) ? vbat.join(',') : String(vbat);
  const values = raw.split(',');

  const eventType = String(log.rawEventType ?? log.typeofevent).trim();

  const packet = values[eventType === '254' ? 5 : 0]?.trim() || '';

  // Event type 254
  if (eventType === '254' && packet.includes('-')) {
    return parse254Vbat(raw)?.id ?? '-';
  }

  const rowId = packet.split('-')[0].trim();

  return rowId && rowId.toUpperCase() !== 'NA' ? rowId : '-';
};
const showRowId =
  String(user?.dealerId ?? '').trim() === '31278' ||
  String(user?.role ?? '').trim().toLowerCase() === 'superadmin';
  const handleRowsPerPageChange = useCallback((e) => {
    const value = e.target.value;
    setLogsRowsPerPage(value === 'all' ? 'all' : Number(value));
    setLogsCurrentPage(1);
    setGoToPage('');
  }, []);

  const handleGoToPage = useCallback((e) => {
    e.preventDefault();
    const page = parseInt(goToPage);
    if (!isNaN(page) && page >= 1 && page <= totalLogPages) {
      setLogsCurrentPage(page);
      setGoToPage('');
    }
  }, [goToPage, totalLogPages]);

  // Toggle sort handler
  const handleToggleSort = useCallback(() => {
    const newState = !isUnsorted;
    setIsUnsorted(newState);
    if (onToggleSort) {
      onToggleSort(newState);
    }
    setLogsCurrentPage(1);
  }, [isUnsorted, onToggleSort]);

  // Refresh handler - maintains current sort state
  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh(isUnsorted);
    }
  }, [onRefresh, isUnsorted]);

  const handleDownload = useCallback(async () => {
    if (!onDownload || downloadLockRef.current) return;
    downloadLockRef.current = true;
    setDownloadError('');
    setDownloadProgress({ completed: 0, total: 0, included: 0, status: 'Preparing images...' });
    setIsDownloading(true);
    try {
      await onDownload(setDownloadProgress);
    } catch (error) {
      setDownloadError('Download failed. Please try again.');
    } finally {
      downloadLockRef.current = false;
      setIsDownloading(false);
    }
  }, [onDownload]);

  // Generate page numbers for display - FIXED
  const getPageNumbers = useCallback(() => {
    const pages = [];
    const maxVisible = 5;
    const halfVisible = Math.floor(maxVisible / 2);

    if (totalLogPages <= maxVisible) {
      for (let i = 1; i <= totalLogPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, logsCurrentPage - halfVisible);
      let endPage = Math.min(totalLogPages, logsCurrentPage + halfVisible);

      if (logsCurrentPage - halfVisible < 1) {
        endPage = Math.min(totalLogPages, endPage + (halfVisible - logsCurrentPage + 1));
      }
      
      if (logsCurrentPage + halfVisible > totalLogPages) {
        startPage = Math.max(1, startPage - (logsCurrentPage + halfVisible - totalLogPages));
      }

      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (endPage < totalLogPages) {
        if (endPage < totalLogPages - 1) {
          pages.push('...');
        }
        pages.push(totalLogPages);
      }
    }

    return pages;
  }, [logsCurrentPage, totalLogPages]);

  const pageItems = getPageNumbers();

  const getLogResultClass = (result) => {
    if (result === 'PASS') return 'pass';
    if (result === 'FAIL') return 'fail';
    return '';
  };

  const isImageLog = (log) => {
    const eventName = String(log.event || log.typeofevent || '')
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_');

    return ['PICTURE_CAPTURED', 'INITIAL_TEST', 'RANDOM_TEST'].includes(eventName);
  };

  const getClientField = (...fieldNames) => {
    for (const fieldName of fieldNames) {
      const normalizedName = fieldName.toLowerCase().replace(/[^a-z]/g, '');
      const matchingKey = Object.keys(client || {}).find(key => key.toLowerCase().replace(/[^a-z]/g, '') === normalizedName);
      if (matchingKey && client[matchingKey] != null && String(client[matchingKey]).trim()) return client[matchingKey];
    }
    return '';
  };
  const clientState = value => String(value || '').trim().toUpperCase();
  const inState = clientState(getClientField('state'));
  const tagState = clientState(getClientField('tagstate', 'tagState'));
  const hideImageColumn = inState === 'CA' || tagState === 'CA';
  const downloadsImages = ['TX', 'INDIA'].includes(tagState);
  const isSuperadmin = String(user?.role || '').trim().toLowerCase() === 'superadmin';

  const canViewBreathGraph = isSuperadmin || String(user?.dealerId ?? '').trim() === '31278';

  const graphData = selected254Log ? parse254Vbat(selected254Log.rawVbat ?? selected254Log.vbat) : null;

  if (!isOpen || !client) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="logs-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '98vw',
          maxWidth: '1800px',
          height: '95vh',
          background: 'var(--bg-secondary)',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
       {canViewBreathGraph && show254Graph && selected254Log && (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      zIndex: 50,
      background: 'rgba(15, 23, 42, 0.55)',
      backdropFilter: 'blur(3px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '10px',
      overscrollBehavior: 'contain'
    }}
    onClick={(event) => { event.stopPropagation(); setShow254Graph(false); }}
  >
    <section
      role="dialog"
      aria-modal="true"
      aria-labelledby="event-254-graph-title"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          setShow254Graph(false);
        }
      }}
      style={{
        width: '100%',
        maxWidth: '1060px',
        maxHeight: '100%',
        minWidth: 0,
        overscrollBehavior: 'contain',
        overflowY: 'auto',
        background: 'var(--bg-secondary)',
        borderRadius: '14px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        color: 'var(--text-primary)'
      }}
    >

      {/* HEADER */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 18px',
          borderBottom: '1px solid var(--border-light)',
          background: 'var(--bg-tertiary)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <Activity size={22} color="#2563eb" style={{ flexShrink: 0 }} aria-hidden="true" />
          <div style={{ minWidth: 0 }}>
            <h3 id="event-254-graph-title" style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>
              Breath Test Analysis
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: '16px', rowGap: '3px', marginTop: '4px', fontSize: '11px', color: 'var(--text-secondary)' }}>
              <span>Event: {selected254Log.event || selected254Log.typeofevent || '-'}</span>
              <span style={{ color: '#dc2626', fontWeight: 600 }}>BAC: {(selected254Log.bac ?? selected254Log.bacvalue) === '' ? '-' : (selected254Log.bac ?? selected254Log.bacvalue ?? '-')}</span>
              {selected254Log.time && <span>{selected254Log.time}</span>}
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="Close graph"
          onClick={() => setShow254Graph(false)}
          style={{
            width: '32px',
            height: '32px',
            border: '1px solid var(--border-light)',
            borderRadius: '7px',
            background: 'var(--bg-secondary)',
            color: 'var(--text-secondary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
        >
          <X size={17} />
        </button>
      </div>

      {/* CONTENT */}
      <div style={{ padding: '14px 18px' }}>

        {graphData ? (
          <>
            {/* METRICS */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(9, minmax(112px, 1fr))',
                alignItems: 'stretch',
                overflowX: 'auto',
                gap: '8px',
                paddingBottom: '4px',
                marginBottom: '12px'
              }}
            >
              {[
                {
                  label: 'Event ID',
                  value: graphData.id,
                  unit: ''
                },
                {
                  label: 'Blow Pressure',
                  value: graphData.blowStartingPressure,
                  unit: ''
                },
                {
                  label: 'Blow Time',
                  value: graphData.userBlowValue,
                  unit: ''
                },
                 {
                  label: 'Air Volume',
                  value: graphData.airVolume,
                  unit: ''
                },
                {
                  label: 'Total Volume',
                  value: graphData.totalVolume,
                  unit: ''
                },
                {
                  label: 'Alcohol Peak',
                  value: graphData.peakFactor,
                  unit: ''
                },
                {
                  label: 'AUC',
                  value: graphData.auc,
                  unit: ''
                },
                {
                  label: 'Wiper Value',
                  value: graphData.wiperValue,
                  unit: ''
                },
                {
                  label: 'Temperature',
                  value:
                    selected254Log?.ambTemperature ??
                    '-',
                }
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    padding: '10px',
                    border: '1px solid var(--border-light)',
                    borderRadius: '9px',
                    background: 'var(--bg-secondary)'
                  }}
                >
                  <div
                    style={{
                      fontSize: '10px',
                      fontWeight: 600,
                      color: 'var(--text-secondary)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      whiteSpace: 'nowrap',
                      marginBottom: '5px'
                    }}
                  >
                    {item.label}
                  </div>

                  <div
                    style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      color: 'var(--text-primary)'
                    }}
                  >
                    {item.value}
                    {item.unit && (
                      <span
                        style={{
                          marginLeft: '4px',
                          fontSize: '11px',
                          fontWeight: 500,
                          color: 'var(--text-secondary)'
                        }}
                      >
                        {item.unit}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Independent charts retain the same sample positions and their own ADC scales. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: '14px' }}>
              {[
                { key: 'pressure', title: 'Pressure ADC', color: '#2563eb', available: graphData.hasPressure },
                { key: 'alcohol', title: 'Alcohol ADC', color: '#dc2626', available: true },
              ].map(sensor => (
                <section key={sensor.key} aria-label={`${sensor.title} analysis`} style={{ minWidth: 0, border: '1px solid var(--border-light)', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ padding: '12px 14px', background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-light)' }}>
                    <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: sensor.color }}>{sensor.title}</h4>
                    <div style={{ marginTop: '4px', fontSize: '11px', color: 'var(--text-secondary)' }}>Sensor response across 10 samples</div>
                  </div>
                  {sensor.available ? (
                    <>
                      <div style={{ height: '260px', padding: '12px 8px 0' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={graphData.adcValues} margin={{ top: 10, right: 12, bottom: 22, left: 4 }}>
                            <CartesianGrid stroke="var(--border-light)" strokeDasharray="4 4" vertical={false} />
                            <XAxis dataKey="sample" tick={{ fontSize: 11, fill: 'var(--text-secondary)' }} tickLine={false} axisLine={{ stroke: 'var(--border-light)' }} label={{ value: 'Sample', position: 'insideBottom', offset: -12, fontSize: 11, fill: 'var(--text-secondary)' }} />
                            <YAxis domain={[0, 'auto']} allowDecimals={false} width={60} tick={{ fontSize: 11, fill: sensor.color }} tickLine={false} axisLine={false} label={{ value: 'ADC', angle: -90, position: 'insideLeft', fontSize: 11, fill: sensor.color }} />
                            <Tooltip labelFormatter={value => `Sample ${value}`} contentStyle={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-light)', borderRadius: '8px', fontSize: '12px' }} />
                            <Line type="linear" dataKey={sensor.key} name={sensor.title} stroke={sensor.color} strokeWidth={2.5} dot={{ r: 3, fill: sensor.color }} activeDot={{ r: 5 }} isAnimationActive={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      <div style={{ padding: '12px 14px 14px', borderTop: '1px solid var(--border-light)' }}>
                        <h5 style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>{sensor.title} Samples</h5>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '6px' }}>
                          {graphData.adcValues.map(item => (
                            <div key={item.sample} style={{ padding: '8px 4px', textAlign: 'center', borderRadius: '6px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-light)' }}>
                              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginBottom: '4px' }}>S{item.sample}</div>
                              <div style={{ fontSize: '13px', fontWeight: 600, color: sensor.color, fontVariantNumeric: 'tabular-nums' }}>{item[sensor.key]}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div style={{ padding: '50px 20px', textAlign: 'center', fontSize: '12px', color: 'var(--text-secondary)' }}>Pressure readings are not available.</div>
                  )}
                </section>
              ))}
            </div>
          </>
        ) : (
          <div
            style={{
              padding: '45px 20px',
              textAlign: 'center',
              color: 'var(--text-secondary)'
            }}
          >
            <div
              style={{
                fontSize: '28px',
                marginBottom: '10px'
              }}
            >
              📊
            </div>

            <div
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--text-primary)'
              }}
            >
              No graph data available
            </div>

            <div
              style={{
                fontSize: '11px',
                marginTop: '4px'
              }}
            >
              This Event does not contain valid vbat data.
            </div>
          </div>
        )}
      </div>

      {/* FOOTER */}
      {/* <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          padding: '12px 20px',
          borderTop: '1px solid var(--border-light)',
          background: 'var(--bg-tertiary)'
        }}
      >
        <button
          type="button"
          onClick={() => setShow254Graph(false)}
          style={{
            padding: '7px 15px',
            border: '1px solid var(--border-light)',
            borderRadius: '6px',
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Close
        </button>
      </div> */}

    </section>
  </div>
)}
        {isDownloading && (
          <div className="logs-download-progress" role="status" aria-live="polite">
            <div className="logs-download-progress-head">
              <div className="logs-download-loader" />
              <div><strong>Preparing ZIP</strong><span>{downloadProgress.status}</span></div>
            </div>
            <div className="logs-download-progress-track">
              <span style={{ width: `${downloadProgress.total ? (downloadProgress.completed / downloadProgress.total) * 100 : 8}%` }} />
            </div>
            <div className="logs-download-progress-count">
              <span>{downloadProgress.completed} / {downloadProgress.total} images processed</span>
              <span>{downloadProgress.included} included</span>
            </div>
          </div>
        )}
        <div className="logs-modal-header">
          <div>
            <h3>Event Logs</h3>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              {client?.name || client?.client || 'Device'} - {client?.sno || client?.serialNumber || ''}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Sort Toggle Button */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text-secondary)' }}>
                {isUnsorted ? 'Unsorted' : 'Sorted'}
              </span>
              <label className="switch" style={{ position: 'relative', display: 'inline-block', width: '40px', height: '22px' }}>
                <input
                  type="checkbox"
                  checked={isUnsorted}
                  onChange={handleToggleSort}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span
                  className="slider round"
                  style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: isUnsorted ? '#2563eb' : '#ccc',
                    transition: '.3s',
                    borderRadius: '22px'
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      content: '""',
                      height: '16px',
                      width: '16px',
                      left: isUnsorted ? '22px' : '3px',
                      bottom: '3px',
                      background: 'white',
                      transition: '.3s',
                      borderRadius: '50%'
                    }}
                  />
                </span>
              </label>
              <span style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text-secondary)' }}>
                {isUnsorted ? 'Recent' : 'Time'}
              </span>
            </div>
            <button className="modal-close" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="logs-filter">
          <input
            type="text"
            placeholder="Search by Sampler ID, Control ID, Event, Result..."
            value={logFilter}
            onChange={(e) => { setLogFilter(e.target.value); setLogsCurrentPage(1); }}
          />
          <div className="logs-per-page">
            <span>Show</span>
            <div className="logs-per-page-select">
            <select aria-label="Logs per page" value={logsRowsPerPage} onChange={handleRowsPerPageChange}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
              <option value="all">All</option>
            </select>
            <ChevronDown size={16} aria-hidden="true" />
            </div>
          </div>
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {isRenderingAll && !logsLoading && (
            <div role="status" aria-live="polite" style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>
              Loading logs... {renderedCount} of {paginatedLogs.length}
            </div>
          )}
          {logsLoading || (isRenderingAll && renderedCount === 0) ? (
            <div className="logs-table-scroll logs-table-loading" aria-busy="true" style={{ overflow: 'auto', flex: 1 }}>
              <table className="logs-table">
                  <thead>
                    <tr>
                       {showRowId && <th>Event ID</th>}
                      <th>Sampler ID</th>
                      <th>Control Board ID</th>
                      <th>Time</th>
                      <th>Event</th>
                      <th>BAC</th>
                      <th>Result</th>
                      {!hideImageColumn && <th>Image</th>}
                      {isSuperadmin && (
                        <>
                          <th>Pressure Offset</th>
                          <th>Alcohol Offset</th>
                          <th>Amb Temp</th>
                          <th>Altitude</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                  <SkeletonRows
                  columns={
                    6 +
                    (showRowId ? 1 : 0) +
                    (hideImageColumn ? 0 : 1) +
                    (isSuperadmin ? 4 : 0)
                  }
                  rows={8}
                />
                </tbody>
                {/* <tbody><SkeletonRows columns={6 + (hideImageColumn ? 0 : 1) + (isSuperadmin ? 4 : 0)} rows={8} /></tbody> */}
              </table>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="logs-empty">
              <p>No logs found for this device</p>
            </div>
          ) : (
            <>
              <div
                className="logs-table-scroll"
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'auto'
                }}
              >
                <table className="logs-table">
                 <colgroup>
  {showRowId && <col className="logs-col-rowid" />}
  <col className="logs-col-sampler" />
  <col className="logs-col-control" />
  <col className="logs-col-time" />
  <col className="logs-col-event" />
  <col className="logs-col-bac" />
  <col className="logs-col-result" />
  {!hideImageColumn && <col className="logs-col-image" />}
  {isSuperadmin && (
    <>
      <col className="logs-col-offset" />
      <col className="logs-col-offset" />
      <col className="logs-col-temp" />
      <col className="logs-col-altitude" />
    </>
  )}
</colgroup>
                  <thead>
                    <tr>
                      {showRowId && <th>Event ID</th>}
                      <th>Sampler ID</th>
                      <th>Control Board ID</th>
                      <th>Time</th>
                      <th>Event</th>
                      <th>BAC</th>
                      <th>Result</th>
                      {!hideImageColumn && <th>Image</th>}
                      {isSuperadmin && (
                        <>
                          <th>Pressure Offset</th>
                          <th>Alcohol Offset</th>
                          <th>Amb Temp</th>
                          <th>Altitude</th>
                        </>
                      )}
                    </tr>
                  </thead>
               <tbody>
  {visibleLogs.map((log, idx) => {
    // console.log('LOG DATA:', log);

    return (
      <tr
        key={idx}
        style={{
          height: '34px'
        }}
      >
       {showRowId && (
  <td className="mono-text">
    {getVbatRowId(log)}
  </td>
)}
        <td
          className="mono-text"
          title={log.samplerId || ''}
        >
          {log.samplerId || '-'}
        </td>

        <td
          className="mono-text"
          title={log.controlId || ''}
        >
          {log.controlId || '-'}
        </td>

        <td>{log.time || '-'}</td>

 {/* Event */}
<td className="logs-event-cell">
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      width: '100%'
    }}
  >
    <span
      style={{
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        textAlign: 'center',
        width: '100%',
        minWidth: 0
      }}
    >
      {log.event || log.typeofevent || '-'}
    </span>

    {canViewBreathGraph && String(log.rawEventType ?? log.typeofevent).trim() === '254' && (
      <button
        type="button"
        title="View breath test graph"
        onClick={() => {
          setSelected254Log(log);
          setShow254Graph(true);
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          padding: '4px 7px',
          border: '1px solid #bfdbfe',
          borderRadius: '5px',
          background: '#eff6ff',
          color: '#2563eb',
          fontSize: '10px',
          fontWeight: 600,
          lineHeight: 1,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          flexShrink: 0
        }}
      >
        <span style={{ fontSize: '11px' }}>📈</span>
        Graph
      </button>
    )}
  </div>
</td>

        <td>
           {[40, 41, 42, 43, 44].includes(
    Number(log.rawEventType ?? log.typeofevent)
  )
    ? ''
    : (log.bac || '-')}</td>

        <td>
          <span
            className={`result-badge ${getLogResultClass(log.result)}`}
          >
            {log.result}
          </span>
        </td>

        {!hideImageColumn && (
          <td className="image-cell">
            {isImageLog(log) ? (
              <button
                className="view-image-btn"
                onClick={() => onViewImage(log, getVbatRowId(log))}
                title="View image"
              >
                <Camera size={15} />
                View
              </button>
            ) : null}
          </td>
        )}
        

        {isSuperadmin && (
          <>
            <td>{String(log.rawEventType ?? log.typeofevent).trim() === '254' ? (String(log.pressureOffset ?? '').trim() || '-') : '-'}</td>
            <td>{log.alcoholOffset || '-'}</td>
            <td>{log.ambTemperature || '-'}</td>
            <td>{log.altitude || '-'}</td>
          </>
        )}
      </tr>
    );
  })}
</tbody>
</table>

                <div className="logs-mobile-list">
                  {visibleLogs.map((log, idx) => (
                    <article className="logs-mobile-card" key={`mobile-${idx}`}>
                      <div className="logs-mobile-card-header">
                        <strong>{log.event || 'Event log'}</strong>
                        <span className={`result-badge ${getLogResultClass(log.result)}`}>{log.result || '-'}</span>
                      </div>
                      <div className="logs-mobile-details">
                        {showRowId && <div><span>Row ID</span><b>{getVbatRowId(log)}</b></div>}
                        <div><span>Sampler ID</span><b>{log.samplerId || '-'}</b></div>
                        <div><span>Control Board ID</span><b>{log.controlId || '-'}</b></div>
                        <div><span>Time</span><b>{log.time || '-'}</b></div>
                        <div><span>BAC</span><b>{log.bac || '-'}</b></div>
                        {canViewBreathGraph && String(log.rawEventType ?? log.typeofevent).trim() === '254' && (
                          <div className="logs-mobile-image">
                            <span>Breath Test Analysis</span>
                            <button type="button" className="view-image-btn" title="View breath test graph"
                              onClick={() => { setSelected254Log(log); setShow254Graph(true); }}>
                              Graph
                            </button>
                          </div>
                        )}
                        {!hideImageColumn && isImageLog(log) && (
                          <div className="logs-mobile-image"><span>Image</span><button className="view-image-btn" onClick={() => onViewImage(log, getVbatRowId(log))}><Camera size={15} /> View</button></div>
                        )}
                        {isSuperadmin && <>
                          <div><span>Pressure Offset</span><b>{String(log.rawEventType ?? log.typeofevent).trim() === '254' ? (String(log.pressureOffset ?? '').trim() || '-') : '-'}</b></div>
                          <div><span>Alcohol Offset</span><b>{log.alcoholOffset || '-'}</b></div>
                          <div><span>Ambient Temperature</span><b>{log.ambTemperature || '-'}</b></div>
                          <div><span>Altitude</span><b>{log.altitude || '-'}</b></div>
                        </>}
                      </div>
                    </article>
                  ))}
                </div>
              </div>

              {/* PAGINATION - IMPROVED MOBILE WITH CENTERED CONTROLS */}
              <div
                className="logs-pagination-bar"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  borderTop: '1px solid var(--border-light)',
                  background: 'var(--bg-secondary)',
                  flexWrap: 'wrap',
                  minHeight: '56px'
                }}
              >
                {/* Desktop Pagination Info - Left */}
                <div
                  className="logs-pagination-info desktop-only"
                  style={{
                    fontSize: '13px',
                    color: '#6b7280',
                    fontWeight: '500',
                    flex: '0 0 auto'
                  }}
                >
                  Showing <strong>{startIndex}</strong> to <strong>{endIndex}</strong> of <strong>{totalItems}</strong>
                </div>

                {/* Pagination Controls - Center */}
                <div
                  className="logs-pagination-controls"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    flex: '1 1 auto'
                  }}
                >
                  <button
                    className="logs-page-nav"
                    style={{
                      minWidth: '32px',
                      height: '32px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: 'var(--bg-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: logsCurrentPage === 1 || totalLogPages === 1 ? 0.4 : 1,
                      pointerEvents: logsCurrentPage === 1 || totalLogPages === 1 ? 'none' : 'auto',
                      fontSize: '12px'
                    }}
                    onClick={() => handleLogsPageChange(1)}
                    disabled={logsCurrentPage === 1 || totalLogPages === 1}
                  >
                    <ChevronsLeft size={14} />
                  </button>

                  <button
                    className="logs-page-nav"
                    style={{
                      minWidth: '32px',
                      height: '32px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: 'var(--bg-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: logsCurrentPage === 1 || totalLogPages === 1 ? 0.4 : 1,
                      pointerEvents: logsCurrentPage === 1 || totalLogPages === 1 ? 'none' : 'auto',
                      fontSize: '12px'
                    }}
                    onClick={() => handleLogsPageChange(logsCurrentPage - 1)}
                    disabled={logsCurrentPage === 1 || totalLogPages === 1}
                  >
                    <ChevronLeft size={14} />
                  </button>

                  {/* Page Numbers - Desktop */}
                  <span className="desktop-only" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {pageItems.map((p, i) => {
                      if (p === '...') {
                        return <span key={`ellipsis-${i}`} style={{ padding: '0 2px', color: '#6b7280', fontSize: '12px' }}>…</span>;
                      }
                      const isActive = logsCurrentPage === p;
                      return (
                        <button
                          key={`page-${p}`}
                          className={`logs-page-number ${isActive ? 'active' : ''}`}
                          onClick={() => handleLogsPageChange(p)}
                          style={{
                            minWidth: '30px',
                            height: '30px',
                            borderRadius: '6px',
                            border: '1px solid #d1d5db',
                            cursor: 'pointer',
                            background: isActive ? '#2563eb' : 'var(--bg-secondary)',
                            color: isActive ? '#fff' : 'var(--text-primary)',
                            fontWeight: isActive ? '600' : '400',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px'
                          }}
                        >
                          {p}
                        </button>
                      );
                    })}
                  </span>

                  {/* Mobile: Current Page / Total Pages - CENTERED */}
                  <span 
                    className="mobile-only" 
                    style={{
                      display: 'none',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      padding: '0 8px',
                      minWidth: '50px',
                      textAlign: 'center'
                    }}
                  >
                    {logsCurrentPage}/{totalLogPages}
                  </span>

                  <button
                    className="logs-page-nav"
                    style={{
                      minWidth: '32px',
                      height: '32px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: 'var(--bg-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: logsCurrentPage === totalLogPages || totalLogPages === 1 ? 0.4 : 1,
                      pointerEvents: logsCurrentPage === totalLogPages || totalLogPages === 1 ? 'none' : 'auto',
                      fontSize: '12px'
                    }}
                    onClick={() => handleLogsPageChange(logsCurrentPage + 1)}
                    disabled={logsCurrentPage === totalLogPages || totalLogPages === 1}
                  >
                    <ChevronRight size={14} />
                  </button>

                  <button
                    className="logs-page-nav"
                    style={{
                      minWidth: '32px',
                      height: '32px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      background: 'var(--bg-secondary)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: logsCurrentPage === totalLogPages || totalLogPages === 1 ? 0.4 : 1,
                      pointerEvents: logsCurrentPage === totalLogPages || totalLogPages === 1 ? 'none' : 'auto',
                      fontSize: '12px'
                    }}
                    onClick={() => handleLogsPageChange(totalLogPages)}
                    disabled={logsCurrentPage === totalLogPages || totalLogPages === 1}
                  >
                    <ChevronsRight size={14} />
                  </button>
                </div>

                {/* Go To Page - Right */}
                <form
                  className="logs-pagination-goto desktop-only"
                  onSubmit={handleGoToPage}
                  style={{
                    display: 'flex',
                    gap: '4px',
                    alignItems: 'center',
                    flex: '0 0 auto'
                  }}
                >
                  <input
                    type="number"
                    min="1"
                    max={totalLogPages}
                    value={goToPage}
                    onChange={(e) => setGoToPage(e.target.value)}
                    placeholder="Go to"
                    disabled={totalLogPages === 1}
                    style={{
                      width: '70px',
                      height: '30px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      padding: '0 8px',
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      opacity: totalLogPages === 1 ? 0.5 : 1,
                      fontSize: '12px'
                    }}
                  />
                  <button
                    type="submit"
                    disabled={totalLogPages === 1}
                    style={{
                      height: '30px',
                      padding: '0 12px',
                      border: 'none',
                      borderRadius: '6px',
                      background: '#2563eb',
                      color: '#fff',
                      cursor: totalLogPages === 1 ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      opacity: totalLogPages === 1 ? 0.5 : 1,
                      fontSize: '12px'
                    }}
                  >
                    Go
                  </button>
                </form>
              </div>

              {/* Mobile Pagination Info - Below controls, centered */}
              <div 
                className="mobile-only" 
                style={{
                  display: 'none',
                  fontSize: '11px',
                  color: '#6b7280',
                  textAlign: 'center',
                  padding: '4px 0 8px 0',
                  borderTop: '1px solid var(--border-light)',
                  background: 'var(--bg-secondary)',
                  width: '100%',
                  justifyContent: 'center'
                }}
              >
                Showing {startIndex} - {endIndex} of {totalItems}
              </div>
            </>
          )}
        </div>

        <div
          className="logs-modal-footer"
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            padding: '12px 16px',
            borderTop: '1px solid #e5e7eb',
            background: 'var(--bg-secondary)',
            flexShrink: 0,
            flexWrap: 'wrap'
          }}
        >
          <button 
            className="btn-download" 
            onClick={handleDownload}
            disabled={isDownloading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              background: '#2d6a4f',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isDownloading ? 'wait' : 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              flex: '1 1 auto',
              minWidth: '80px',
              justifyContent: 'center'
            }}
          >
            <Download size={14} />
            <span className="btn-text">{isDownloading ? 'Preparing...' : downloadsImages ? 'Download ZIP' : 'Download CSV'}</span>
          </button>
          {downloadError && <span className="logs-download-error">{downloadError}</span>}
          <button 
            className="btn-refresh" 
            onClick={handleRefresh}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              flex: '1 1 auto',
              minWidth: '80px',
              justifyContent: 'center'
            }}
          >
            <RefreshCw size={14} />
            <span className="btn-text">Refresh</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogsModal;
